import React from 'react'
import { graphql } from 'gatsby'

// components
import BlockRenderer from '../components/BlockRenderer'
import PageWrap from '../components/Layouts/PageWrap'
import HeaderSuccessStory from '../components/SuccessStory/HeaderSuccessStory'
import SuccessStoryOverview from '../components/SuccessStory/SuccessStoryOverview'
import Image, { getImageBySize } from '../components/Images/Image'
import CardGroup from '../components/Cards/CardGroup'

// layouts
import Offset from '../components/Layouts/Offset'
import SectionContainer from '../components/Layouts/SectionContainer'

// utilities
import { transformData as transformCardData } from '../components/Cards/Card'

const transformHeaderData = data => {
  return {
    labels: [
      {
        label: 'Success Story',
        url: '/success-stories',
      },
    ],
    heading: data.title,
    subheading: data?.acfSuccessStory?.secondaryHeading,
    tags:
      data.successStoryCategories &&
      data.successStoryCategories.nodes?.map(category => ({
        url: `/success-stories#${category.slug}`,
        label: category.name,
      })),
  }
}

const transformOverviewData = data => {
  const acf = data?.acfSuccessStory

  return {
    heading: acf?.overviewTitle,
    content: acf?.overviewContent,
    image: {
      src: acf?.overviewImage?.mediaItemUrl,
      alt: acf?.overviewImage?.altText,
    },
    stats: acf?.cardStats?.map(c => {
      return {
        pct: c.number,
        label: c.text,
      }
    }),
    testimonial: acf.quote && {
      quote: acf?.quote,
      person: {
        name: acf?.authorName,
        org: acf?.authorTitle,
      },
    },
  }
}

const SuccessStory = ({ data }) => {
  const featuredImage =
    data?.page?.featuredImage?.node &&
    getImageBySize(data.page.featuredImage.node, 'post-feature')

  return (
    <PageWrap seo={data?.page?.seo} noFlex>
      <SectionContainer>
        <HeaderSuccessStory {...transformHeaderData(data.page)} />
      </SectionContainer>

      {featuredImage && (
        <SectionContainer>
          {featuredImage.width >= 1410 ? (
            <Offset inline>
              <Image {...featuredImage} />
            </Offset>
          ) : (
            <div className="container flex items-start justify-center">
              <Image {...featuredImage} />
            </div>
          )}
        </SectionContainer>
      )}

      {data.page.acfSuccessStory.overviewTitle && (
        <SectionContainer>
          <SuccessStoryOverview {...transformOverviewData(data.page)} />
        </SectionContainer>
      )}

      <SectionContainer as="div" className="container max-w-screen-md">
        <BlockRenderer blocks={data.page.blocks} />
      </SectionContainer>

      <SectionContainer className="container">
        <CardGroup
          sectionHeader={{ heading: 'More Success Stories' }}
          cards={data.moreStories.nodes.map(post => ({
            ...transformCardData(post),
            subheading: post.acfSuccessStory.taglineShort,
            stat: post.acfSuccessStory.cardStats?.map(stat => ({
              label: stat.text,
              pct: stat.number,
            }))[0],
            logo: post.acfSuccessStory.cardLogo && {
              src: post.acfSuccessStory.cardLogo.sourceUrl,
              alt: post.acfSuccessStory.cardLogo.altText,
            },
            image:
              post.featuredImage &&
              getImageBySize(post.featuredImage.node, 'content-card'),
          }))}
        />
      </SectionContainer>
    </PageWrap>
  )
}

export default SuccessStory

export const query = graphql`
  query($id: String!) {
    page: wpSuccessStory(id: { eq: $id }) {
      seo {
        ...seoPostTypeFields
      }
      title
      acfSuccessStory {
        cardStats {
          number
          text
        }
        taglineShort
        secondaryHeading
        overviewTitle
        overviewContent
        quote
        authorName
        authorTitle
        overviewImage {
          altText
          mediaItemUrl
        }
      }
      successStoryCategories {
        nodes {
          slug
          uri
          name
        }
      }
      featuredImage {
        node {
          ...mediaItemFields
        }
      }
      blocks {
        ...blockFields
      }
    }

    moreStories: allWpSuccessStory(limit: 2, filter: { id: { ne: $id } }) {
      nodes {
        title
        uri
        featuredImage {
          node {
            ...mediaItemFields
          }
        }
        acfSuccessStory {
          cardStats {
            text
            number
          }
          taglineShort
          cardLogo {
            sourceUrl
            altText
          }
        }
      }
    }
  }
`
